import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular/standalone';
import { from, catchError, switchMap, throwError } from 'rxjs';
import { authService } from './auth.service';
import { AppConfig } from '../config/app-config';

const AUTH_RETRY_HEADER = 'x-bm-auth-retry';

/**
 * Global HTTP error handler.
 *  401 — clears auth state and redirects to login.
 *  5xx — shows a generic error toast.
 * All other errors are re-thrown for components to handle locally.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router    = inject(Router);
  const toastCtrl = inject(ToastController);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) return throwError(() => err);

      if (err.status === 401) {
        const canRetry =
          req.url.startsWith(AppConfig.apiBaseUrl) &&
          authService.isAuthenticated() &&
          !req.headers.has(AUTH_RETRY_HEADER);

        if (canRetry) {
          return from(authService.forceRefresh()).pipe(
            switchMap(() => next(req.clone({
              setHeaders: { [AUTH_RETRY_HEADER]: '1' },
            }))),
            catchError(() => {
              authService.logout();
              void router.navigateByUrl('/login', { replaceUrl: true });
              return throwError(() => err);
            }),
          );
        }

        authService.logout();
        void router.navigateByUrl('/login', { replaceUrl: true });
      } else if (err.status >= 500) {
        void toastCtrl
          .create({
            message: 'Something went wrong. Please try again.',
            duration: 3000,
            position: 'top',
            cssClass: 'bevme-confirmation-toast bevme-confirmation-toast--error',
          })
          .then(t => t.present());
      }

      return throwError(() => err);
    }),
  );
};
